import {
  ACADEMIC_TERMS,
  graduationYearOptions,
  programStartYearOptions,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { OnboardingQuestion } from '../onboarding.types';

const STRINGS = appStrings.modules.onboarding.questions;

const ROLE_SELECTION_STRINGS = STRINGS.roleSelection;
const COMPLETE_ACCOUNT_STRINGS = STRINGS.completeAccount;
const ACTIVATION_CODE_STRINGS = STRINGS.activationCode;
const SELECT_PLAN_STRINGS = STRINGS.selectPlan;
const INVITE_FAMILY_STRINGS = STRINGS.inviteFamily;

export const HIGH_SCHOOL_ENROLLMENT_TYPE = '1';

/**
 * Whether the academic questions apply to this answer set. '6' is the
 * "Not currently a student" enrollment type.
 */
const isAskedOfStudent = (formValues: Record<string, unknown>) => {
  if (formValues.academicEnrollmentTypes === '6') return false;

  const role = (formValues as Record<string, string>).ROLE_SELECTION;

  return !role || role === 'student';
};

const isHighSchoolEnrollment = (formValues: Record<string, unknown>) =>
  formValues.academicEnrollmentTypes === HIGH_SCHOOL_ENROLLMENT_TYPE;

type QuestionKey =
  | 'ROLE_SELECTION'
  | 'COMPLETE_ACCOUNT'
  | 'ACTIVATION_CODE'
  | 'SELECT_PLAN'
  | 'INVITE_FAMILY';

export const ONBOARDING_QUESTIONS = (): Record<QuestionKey, OnboardingQuestion> => {
  return {
    ROLE_SELECTION: {
      title: ROLE_SELECTION_STRINGS.title,
      subTitle: ROLE_SELECTION_STRINGS.subtitle,
      featuredIcon: 'User01',
      onComplete: 'POST_PROFILE_OPTION_ANSWER',
      question: {
        type: 'RADIO',
        profile_option_category_id: 'onboarding',
        profile_option_question_id: 'onboarding_role',
        rules: {
          required: ROLE_SELECTION_STRINGS.ruleHelperText,
        },
        options: [
          { label: ROLE_SELECTION_STRINGS.options.student, value: 'student' },
          { label: ROLE_SELECTION_STRINGS.options.youngAdult, value: 'young_adult' },
          { label: ROLE_SELECTION_STRINGS.options.parentGuardian, value: 'parent_guardian' },
          { label: ROLE_SELECTION_STRINGS.options.educator, value: 'educator' },
          { label: ROLE_SELECTION_STRINGS.options.employer, value: 'employer' },
          { label: ROLE_SELECTION_STRINGS.options.other, value: 'other' },
        ],
      },
    },
    COMPLETE_ACCOUNT: {
      title: COMPLETE_ACCOUNT_STRINGS.title,
      subTitle: COMPLETE_ACCOUNT_STRINGS.subTitle,
      featuredIcon: 'User01',
      onComplete: 'POST_PROFILE_OPTION',
      question: {
        type: 'FORM',
        fields: [
          {
            name: 'zip_code',
            placeholder: COMPLETE_ACCOUNT_STRINGS.zipCode.placeholder,
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
              required: COMPLETE_ACCOUNT_STRINGS.zipCode.ruleHelperText,
            },
          },
          {
            name: 'academicEnrollmentTypes',
            placeholder: COMPLETE_ACCOUNT_STRINGS.enrollmentType.placeholder,
            type: 'text',
            rules: {
              required: COMPLETE_ACCOUNT_STRINGS.enrollmentType.ruleHelperText,
            },

            post_type: 'PROFILE_OPTION',

            // Only show for students
            showIf: (values) => values.ROLE_SELECTION === 'student',

            selectOptions: {
              renderAsSelect: true,
              type: 'SINGLE',

              options: [
                { label: COMPLETE_ACCOUNT_STRINGS.enrollmentType.options.highSchool, value: '1' },
                { label: COMPLETE_ACCOUNT_STRINGS.enrollmentType.options.trade, value: '2' },
                { label: COMPLETE_ACCOUNT_STRINGS.enrollmentType.options.college, value: '4' },
                {
                  label: COMPLETE_ACCOUNT_STRINGS.enrollmentType.options.communityCollege,
                  value: '3',
                },
                { label: COMPLETE_ACCOUNT_STRINGS.enrollmentType.options.graduate, value: '5' },
              ],
            },
          },
          {
            name: 'academicsCurrentEnrollment',
            placeholder: COMPLETE_ACCOUNT_STRINGS.currentEnrollment.placeholder,
            type: 'text',
            autoCompleteEndpoint: '/profile/lookups/schools',
            post_type: 'PROFILE_OPTION',

            // Only show for students
            showIf: (values) => values.ROLE_SELECTION === 'student',

            selectOptions: {
              type: 'SINGLE',
              renderAsSelect: true,
              options: [],
            },
          },
          {
            name: 'academicProgramStartTerm',
            placeholder: COMPLETE_ACCOUNT_STRINGS.programStartTerm.placeholder,
            type: 'text',
            rules: {
              validate: (value: string, formValues: Record<string, unknown>) => {
                if (!isAskedOfStudent(formValues) || isHighSchoolEnrollment(formValues)) {
                  return true;
                }

                return value ? true : COMPLETE_ACCOUNT_STRINGS.programStartTerm.ruleHelperText;
              },
            },

            post_type: 'PROFILE_OPTION',

            showIf: (values) =>
              values.ROLE_SELECTION === 'student' && !isHighSchoolEnrollment(values),

            selectOptions: {
              renderAsSelect: true,
              type: 'SINGLE',

              options: ACADEMIC_TERMS.map((term) => ({
                label: term,
                value: term,
              })),
            },
          },
          {
            name: 'academicProgramStartYear',
            placeholder: COMPLETE_ACCOUNT_STRINGS.programStartYear.placeholder,
            type: 'text',
            rules: {
              validate: (value: string, formValues: Record<string, unknown>) => {
                if (!isAskedOfStudent(formValues)) return true;

                return value ? true : COMPLETE_ACCOUNT_STRINGS.programStartYear.ruleHelperText;
              },
            },

            post_type: 'PROFILE_OPTION',

            // Only show for students
            showIf: (values) => values.ROLE_SELECTION === 'student',

            selectOptions: {
              renderAsSelect: true,
              type: 'SINGLE',

              options: programStartYearOptions().map((year) => ({
                label: year.toString(),
                value: year.toString(),
              })),
            },
          },
          {
            name: 'academicExpectedGraduationYear',
            placeholder: COMPLETE_ACCOUNT_STRINGS.expectedGraduation.placeholder,
            type: 'text',
            rules: {
              validate: (value: string, formValues: Record<string, unknown>) => {
                if (!isAskedOfStudent(formValues)) return true;

                if (!value) return COMPLETE_ACCOUNT_STRINGS.expectedGraduation.ruleHelperText;

                const startYear = Number(formValues.academicProgramStartYear);

                return !startYear || Number(value) > startYear
                  ? true
                  : COMPLETE_ACCOUNT_STRINGS.expectedGraduation.afterStartHelperText;
              },
            },

            post_type: 'PROFILE_OPTION',

            // Only show for students
            showIf: (values) => values.ROLE_SELECTION === 'student',

            selectOptions: {
              renderAsSelect: true,
              type: 'SINGLE',

              options: graduationYearOptions().map((year) => ({
                label: year.toString(),
                value: year.toString(),
              })),
            },
          },
        ],
      },
    },
    ACTIVATION_CODE: {
      title: ACTIVATION_CODE_STRINGS.title,
      subTitle: ACTIVATION_CODE_STRINGS.subTitle,
      featuredIcon: 'Tag01',
      onComplete: 'CONTINUE_WITHOUT_SUBMIT',
      question: {
        type: 'FORM',
        fields: [
          {
            name: 'invite_code',
            placeholder: ACTIVATION_CODE_STRINGS.placeholder,
            type: 'text',
            post_type: 'USER',
          },
        ],
      },
      subAction: {
        body: ACTIVATION_CODE_STRINGS.subactionTitle,
        actionText: ACTIVATION_CODE_STRINGS.subactionText,
        onClick: 'SKIP',
      },
    },
    SELECT_PLAN: {
      title: SELECT_PLAN_STRINGS.title,
      subTitle: SELECT_PLAN_STRINGS.subTitle,
      featuredIcon: 'Tag01',
      onComplete: 'CONTINUE_WITHOUT_SUBMIT',
      question: {
        type: 'FORM',
        fields: [],
      },
    },
    INVITE_FAMILY: {
      title: INVITE_FAMILY_STRINGS.title,
      subTitle: INVITE_FAMILY_STRINGS.subTitle,
      featuredIcon: 'Mail05',
      onComplete: 'CONTINUE_WITHOUT_SUBMIT',
      question: {
        type: 'FORM',
        fields: [],
      },
    },
  };
};
